import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import * as XLSX from 'xlsx';
import '../../css/Pages.css';
import BookCompilationListing from '../../components/Register/BookCompilation/BookCompilationListing';
import BookCompilationForm from '../../components/Register/BookCompilation/BookCompilationForm';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';

const BookCompilation = () => {
    const toast = useRef(null);

    const today = new Date().toISOString().split('T')[0];
    const initialForm = {
        ProjectTypeId: '', BookName: '', TargetAudience: '', Objectives: '', EstimatedStructure: '',
        IsFirstTime: true, OldPublishYear: '', ReasonForRewrite: '', StartDate: today, EndDate: today,
        EstimatedMonths: '', CourseCode: '', CreditCount: '', BookOutline: '', AttachmentUrl: '', Notes: ''
    };

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [projectTypes, setProjectTypes] = useState([]);
    const [members, setMembers] = useState([]);
    const [users, setUsers] = useState([]);

    const [searchQuery, setSearchQuery] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'BookName', direction: 'asc' });
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState(initialForm);
    const [editId, setEditId] = useState(null);

    const { confirmDeleteDialog } = useConfirmDeleteDialog();

    const getAuthHeaders = useCallback(() => {
        return { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
    }, []);

    const handleLogout = useCallback(() => {
        toast.current?.show({ severity: 'error', summary: 'Lỗi xác thực', detail: 'Phiên đăng nhập hết hạn hoặc không có quyền truy cập!', life: 3000 });
        setTimeout(() => {
            localStorage.removeItem('user');
            localStorage.removeItem('accessToken');
            window.location.href = '/login';
        }, 1500);
    }, []);

    const fetchProjectTypes = useCallback(async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/projecttype`, {
                headers: getAuthHeaders()
            });
            if (res.status === 401 || res.status === 403) return handleLogout();
            if (res.ok) setProjectTypes(await res.json());
        } catch (error) { console.error(error); }
    }, [getAuthHeaders, handleLogout]);

    const fetchUsers = useCallback(async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user?mode=selection`, {
                headers: getAuthHeaders()
            });
            if (res.status === 401 || res.status === 403) return handleLogout();
            if (res.ok) setUsers(await res.json());
        } catch (error) { console.error(error); }
    }, [getAuthHeaders, handleLogout]);

    const fetchData = useCallback(async (isBackground = false) => {
        if (!isBackground) setIsLoading(true);
        try {
            const currentUser = JSON.parse(localStorage.getItem('user')) || {};
            if (!currentUser || !currentUser.Id) {
                if (toast.current) toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Phiên đăng nhập hết hạn!', life: 3000 });
                handleLogout();
                if (!isBackground) setIsLoading(false);
                return;
            }

            const userRoleName = (currentUser?.RoleName || '').toLowerCase();
            const isManager = currentUser?.RoleId === 1 || userRoleName === 'quản trị viên' ||
                ((currentUser?.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser?.IsDelegatedAdmin === true);

            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/bookcompilation?userId=${currentUser.Id}&isAdmin=${isManager}`,
                {
                    method: 'GET',
                    headers: getAuthHeaders()
                }
            );

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            if (response.ok) {
                const result = await response.json();
                setData(result);
            }
        } catch (error) {
            if (toast.current) toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi tải dữ liệu!', life: 3000 });
        } finally {
            if (!isBackground) setIsLoading(false);
        }
    }, [getAuthHeaders, handleLogout]);

    useEffect(() => {
        fetchData();
        fetchProjectTypes();
        fetchUsers();
    }, [fetchData, fetchProjectTypes, fetchUsers]);

    useEffect(() => {
        let result = [...data];

        if (searchQuery) {
            result = result.filter(item =>
                (item.BookName && item.BookName.toLowerCase().includes(searchQuery))
            );
        }

        if (sortConfig !== null) {
            result.sort((a, b) => {
                const valA = (a[sortConfig.key] || '').toString().trim();
                const valB = (b[sortConfig.key] || '').toString().trim();
                const comparison = valA.localeCompare(valB, 'vi', { sensitivity: 'base' });
                return sortConfig.direction === 'asc' ? comparison : -comparison;
            });
        }

        setFilteredData(result);
    }, [data, searchQuery, sortConfig]);

    const handleSearch = (e) => {
        setSearchQuery(e.target.value.toLowerCase());
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            const currentUser = JSON.parse(localStorage.getItem('user')) || {};
            const userId = currentUser ? currentUser.Id : null;
            const userRoleName = (currentUser?.RoleName || '').toLowerCase();
            const isManager = currentUser?.RoleId === 1 || userRoleName === 'quản trị viên' ||
                ((currentUser?.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser?.IsDelegatedAdmin === true);

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/bookcompilation?isAdmin=${isManager}&userId=${userId}`, {
                method: 'PATCH',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json; charset=UTF-8'
                },
                body: JSON.stringify({ id: id, status: newStatus })
            });

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            if (response.ok) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: newStatus === 'Đã gửi duyệt' ? "Gửi yêu cầu duyệt thành công!" : "Hủy yêu cầu duyệt thành công!",
                    life: 3000
                });
                fetchData(true);
            } else {
                let errData;
                try { errData = await response.json(); } catch (e) { errData = { message: "Thao tác thất bại!" }; }
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: errData.message, life: 3000 });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        }
    };

    const handleBulkStatusChange = async (ids, newStatus) => {
        if (newStatus === 'Đã gửi duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
            if (invalidItems.length > 0) {
                return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã gửi hoặc đã duyệt để có thể thao tác!', life: 4000 });
            }
        } else if (newStatus === 'Lưu nháp') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status !== 'Đã gửi duyệt');
            if (invalidItems.length > 0) {
                return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Chỉ có thể hủy đối với hồ sơ có trạng thái "Đã gửi duyệt"!', life: 4000 });
            }
        }

        setIsLoading(true);
        try {
            const currentUser = JSON.parse(localStorage.getItem('user')) || {};
            const userId = currentUser ? currentUser.Id : null;
            const userRoleName = (currentUser?.RoleName || '').toLowerCase();
            const isManager = currentUser?.RoleId === 1 || userRoleName === 'quản trị viên' ||
                ((currentUser?.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser?.IsDelegatedAdmin === true);

            const promises = ids.map(id =>
                fetch(`${process.env.REACT_APP_API_BASE_URL}/bookcompilation?isAdmin=${isManager}&userId=${userId}`, {
                    method: 'PATCH',
                    headers: {
                        ...getAuthHeaders(),
                        'Content-Type': 'application/json; charset=UTF-8'
                    },
                    body: JSON.stringify({ id: id, status: newStatus })
                })
            );

            const results = await Promise.all(promises);
            const allOk = results.every(res => res.ok);

            if (allOk) {
                toast.current.show({
                    severity: 'success', summary: 'Thành công',
                    detail: newStatus === 'Đã gửi duyệt' ? `Đã gửi yêu cầu duyệt ${ids.length} mục!` : `Đã hủy gửi duyệt ${ids.length} mục!`, life: 3000
                });
            } else {
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: "Một số thao tác thất bại!", life: 3000 });
            }
            fetchData(true);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (editId) {
            const currentItem = data.find(d => d.Id === editId);
            if (currentItem?.Status === 'Đã gửi duyệt' || currentItem?.Status === 'Đã duyệt') {
                return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Hồ sơ đã khóa, không thể lưu!', life: 3000 });
            }
        }

        const currentUser = JSON.parse(localStorage.getItem('user')) || {};
        if (!currentUser || !currentUser.Id) {
            return handleLogout();
        }

        const method = editId ? 'PUT' : 'POST';

        const payload = {
            ...formData,
            UserId: currentUser.Id,
            ProjectTypeId: parseInt(formData.ProjectTypeId),
            MembersJSON: JSON.stringify(members)
        };

        if (formData.OldPublishYear) payload.OldPublishYear = parseInt(formData.OldPublishYear);
        if (formData.EstimatedMonths) payload.EstimatedMonths = parseInt(formData.EstimatedMonths);
        if (formData.CreditCount) payload.CreditCount = parseInt(formData.CreditCount);
        if (editId) payload.Id = editId;

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/bookcompilation`, {
                method,
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json; charset=UTF-8'
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            let result;
            try {
                result = await response.json();
            } catch (err) {
                result = { status: 'error', message: 'Lỗi máy chủ không xác định!' };
            }

            if (response.ok && result.status !== 'error') {
                toast.current.show({ severity: 'success', summary: 'Thành công', detail: editId ? 'Cập nhật thành công!' : 'Thêm mới thành công!', life: 3000 });
                fetchData(true);
                closeModal();
            } else {
                const errorMsg = result.message || 'Lưu dữ liệu thất bại!';
                toast.current.show({ severity: 'error', summary: 'Không thể lưu', detail: errorMsg, life: 6000 });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        }
    };

    const handleEdit = async (item) => {
        let parsedMembers = [];
        try {
            parsedMembers = JSON.parse(item.MembersJSON || '[]');
        } catch (e) {
            parsedMembers = [];
        }

        const missingIds = parsedMembers
            .map(m => m.userId || m.UserId || m.Id || m.id)
            .filter(id => id && !users.find(u => u.Id.toString() === id.toString()));

        if (missingIds.length > 0) {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user?includeIds=${missingIds.join(',')}`, {
                    headers: getAuthHeaders()
                });

                if (res.status === 401 || res.status === 403) {
                    handleLogout();
                    return;
                }

                if (res.ok) {
                    const extraUsers = await res.json();

                    setUsers(prevUsers => {
                        const merged = [...prevUsers];
                        extraUsers.forEach(eu => {
                            if (!merged.find(u => u.Id === eu.Id)) {
                                merged.push(eu);
                            }
                        });
                        return merged;
                    });
                }
            } catch (error) {
                console.error("Lỗi khi tải thông tin người dùng bổ sung:", error);
            }
        }

        setEditId(item.Id);
        setFormData({ ...item });
        setMembers(parsedMembers);
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        const item = data.find(d => d.Id === id);
        if (item?.Status === 'Đã gửi duyệt' || item?.Status === 'Đã duyệt') {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không thể xóa hồ sơ đã gửi duyệt hoặc đã duyệt!', life: 3000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa',
            message: 'Bạn có chắc chắn muốn xóa đăng ký này?',
            accept: async () => {
                try {
                    const currentUser = JSON.parse(localStorage.getItem('user')) || {};
                    const userId = currentUser ? currentUser.Id : null;
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/bookcompilation?id=${id}&userId=${userId}`, {
                        method: 'DELETE',
                        headers: getAuthHeaders()
                    });

                    if (response.status === 401 || response.status === 403) {
                        handleLogout();
                        return;
                    }

                    if (response.ok) {
                        toast.current.show({ severity: 'success', summary: 'Thành công', detail: 'Đã xóa đăng ký!' });
                        fetchData(true);
                    } else {
                        let errData;
                        try { errData = await response.json(); } catch (e) { errData = { message: "Xóa thất bại!" }; }
                        toast.current.show({ severity: 'error', summary: 'Lỗi', detail: errData.message });
                    }
                } catch (error) {
                    toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' });
                }
            }
        });
    };

    const handleBulkDelete = (ids) => {
        const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
        if (invalidItems.length > 0) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã gửi hoặc đã duyệt khỏi danh sách cần xóa!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa hàng loạt',
            message: `Bạn có chắc chắn muốn xóa ${ids.length} đăng ký đã chọn?`,
            accept: async () => {
                setIsLoading(true);
                try {
                    const currentUser = JSON.parse(localStorage.getItem('user')) || {};
                    const userId = currentUser ? currentUser.Id : null;

                    const promises = ids.map(id =>
                        fetch(`${process.env.REACT_APP_API_BASE_URL}/bookcompilation?id=${id}&userId=${userId}`, {
                            method: 'DELETE',
                            headers: getAuthHeaders()
                        })
                    );

                    const results = await Promise.all(promises);
                    const allOk = results.every(res => res.ok);

                    if (allOk) {
                        toast.current.show({ severity: 'success', summary: 'Thành công', detail: `Đã xóa ${ids.length} bản ghi!` });
                    } else {
                        toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Một số bản ghi xóa thất bại!' });
                    }
                    fetchData(true);
                } catch (error) {
                    toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const closeModal = () => { setIsModalOpen(false); setFormData(initialForm); setMembers([]); setEditId(null); };

    const exportToExcel = () => {
        const dataToExport = filteredData;

        if (dataToExport.length === 0) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có dữ liệu để xuất!', life: 3000 });
        }

        const excelData = dataToExport.map((item, index) => ({
            'STT': index + 1,
            'Tên sách/giáo trình': item.BookName || '',
            'Loại biên soạn': item.IsFirstTime ? 'Biên soạn lần đầu' : 'Viết lại',
            'Đối tượng sử dụng': item.TargetAudience || '',
            'Mã học phần': item.CourseCode || '',
            'Số tín chỉ': item.CreditCount || '',
            'Thời gian BĐ': item.StartDate ? new Date(item.StartDate).toLocaleDateString('vi-VN') : '',
            'Thời gian KT': item.EndDate ? new Date(item.EndDate).toLocaleDateString('vi-VN') : '',
            'Trạng thái': item.Status || 'Lưu nháp',
            'Người tạo': item.CreatedByEmail || ''
        }));

        excelData.push({
            'STT': '',
            'Tên sách/giáo trình': 'TỔNG CỘNG:',
            'Loại biên soạn': `${dataToExport.length} Đăng ký`,
            'Đối tượng sử dụng': '',
            'Mã học phần': '',
            'Số tín chỉ': '',
            'Thời gian BĐ': '',
            'Thời gian KT': '',
            'Trạng thái': '',
            'Người tạo': ''
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "DangKyBienSoan");

        const wscols = [
            { wch: 5 }, { wch: 45 }, { wch: 20 }, { wch: 30 }, { wch: 15 },
            { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 25 }
        ];
        worksheet['!cols'] = wscols;

        XLSX.writeFile(workbook, `DanhSachDangKyBienSoan_${today}.xlsx`);
    };

    const handleAddNew = () => {
        const currentUser = JSON.parse(localStorage.getItem('user')) || {};
        const detailedUser = users.find(u => u.Id?.toString() === currentUser.Id?.toString()) || {};

        setMembers([{
            userId: detailedUser.Id || currentUser.Id || '',
            name: `${currentUser.LastName || ''} ${currentUser.FirstName || ''}`.trim(),
            role: 'Chủ biên',
            title: detailedUser.AcademicTitleName || '',
            degree: detailedUser.DegreeName || '',
            content: ''
        }]);

        setEditId(null);
        setFormData(initialForm);
        setIsModalOpen(true);
    };

    return (
        <div className="page-container">
            <Toast ref={toast} position="top-right" baseZIndex={99999} style={{ zIndex: 99999 }} />
            <div className="page-header"><div className="header-title"><h2 style={{ textTransform: 'uppercase' }}>ĐĂNG KÝ BIÊN SOẠN SÁCH, GIÁO TRÌNH</h2></div></div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                <button className="btn-add-new" onClick={handleAddNew} style={{ margin: 0 }}>
                    <i className="fa-solid fa-plus"></i> Thêm mới
                </button>
                <button
                    className="btn-add-new"
                    style={{ backgroundColor: '#1a9953', margin: 0 }}
                    onClick={exportToExcel}
                >
                    <i className="fa-solid fa-file-excel"></i> Xuất Excel
                </button>
            </div>

            <div style={{ backgroundColor: '#fff', padding: '15px', borderRadius: '5px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                    <p className="sub-title" style={{ margin: 0 }}>DANH SÁCH ĐĂNG KÝ</p>
                    <div className="search-wrapper" style={{ position: 'relative', width: '100%', maxWidth: '350px' }}>
                        <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '10px', top: '12px', color: '#888' }}></i>
                        <input type="text" placeholder="Tìm kiếm" className="form-input" style={{ width: '100%', paddingLeft: '35px' }} value={searchQuery} onChange={handleSearch} />
                    </div>
                </div>
            </div>

            <BookCompilationListing
                data={filteredData}
                searchQuery={searchQuery}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onSendSubmit={(id) => handleStatusChange(id, 'Đã gửi duyệt')}
                onCancelSubmit={(id) => handleStatusChange(id, 'Lưu nháp')}
                onBulkSend={(ids) => handleBulkStatusChange(ids, 'Đã gửi duyệt')}
                onBulkCancel={(ids) => handleBulkStatusChange(ids, 'Lưu nháp')}
                onBulkDelete={handleBulkDelete}
                isLoading={isLoading}
                toast={toast}
                sortConfig={sortConfig}
                requestSort={(key) => {
                    let direction = 'asc';
                    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
                    setSortConfig({ key, direction });
                }}
            />

            <BookCompilationForm
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                isEditing={!!editId}
                isReadOnly={formData.Status === 'Đã duyệt'}
                projectTypes={projectTypes}
                members={members}
                setMembers={setMembers}
                users={users}
                toast={toast}
            />
        </div>
    );
};

export default BookCompilation;